import React, {
  useEffect,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useAtomValue } from 'jotai';
import { useLocation } from '@gatsbyjs/reach-router';
import {
  DynamicIcon,
  type IconName,
} from '@curology/ui-components-web/react/Icon/DynamicIcon';

import { COOKIE_KEYS } from 'consts/cookieKeys';
import APP_URLS from 'consts/appUrls';
import { EVENTS } from 'consts/metricsTrackingEvents';
import { FREE_TRIAL_URLS } from 'consts/freeTrialUrls';
import cookie from 'utils/cookie';
import { navExternalWithQueryString } from 'utils/marketingSafeDomain';
import { trackEvent } from 'utils/metricsTracking';

import { Banner } from 'components/global/Banner';
import { bannerTextAtom } from 'components/pages/VanityRedirect';
import {
  CtaModal,
  CtaModalProps,
  Modal,
  ModalProps,
} from 'components/global/modal';
import { VanityBanner } from '.';
import { Badge, NavBannerBox } from './NavBanner.style';
import { LOCAL_COLORS } from 'styles/constants';
import { Z_SCALE } from 'radiance-ui/lib/constants';
import { ldClientReady, useFlag } from 'utils/featureFlagsAndMetrics';

export interface NavBannerProps {
  iconName?: IconName;
  backgroundColor?: string;
  text?: ReactNode;
  textColor?: string;
  ctaText?: string | ReactElement;
  modalProps?: ModalProps | CtaModalProps;
  showModalOnLoad?: boolean;
  cookieKey?: string;
  isClosable?: boolean;
  floatingOnDesktop?: boolean;
  hideIfVanity?: boolean;
  aboveNavigation?: boolean;
  excludePaths?: string[];
  badge?: ReactNode;
}

interface PromoFlag {
  bannerText: string;
  freeTrialBannerText?: string;
  imageUrl: string;
}

const getBannerText = (promoFlag?: PromoFlag, text?: ReactNode) => {
  if (
    typeof window !== 'undefined' &&
    window?.location?.pathname &&
    FREE_TRIAL_URLS.includes(window.location.pathname)
  ) {
    return promoFlag?.freeTrialBannerText ?? text;
  }

  return promoFlag?.bannerText ?? text;
};

const hasActiveGiftWithPurchase = (promoFlag?: PromoFlag): boolean => {
  return promoFlag?.bannerText !== undefined;
};

export const NavBanner = ({
  iconName,
  backgroundColor = LOCAL_COLORS.surfaceBrandBold,
  text,
  textColor = LOCAL_COLORS.surfaceWhite,
  ctaText,
  modalProps,
  showModalOnLoad = false,
  cookieKey,
  isClosable,
  floatingOnDesktop,
  hideIfVanity = false,
  excludePaths = [],
  badge,
  ...props
}: NavBannerProps) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const vanityBannerText = useAtomValue(bannerTextAtom);

  const location = useLocation();

  const hideBanner = excludePaths.some((pattern) =>
    location.pathname.match(pattern),
  );

  useEffect(() => {
    const hasBanner = !!props || !!vanityBannerText;
    if (hasBanner && cookie.has(COOKIE_KEYS.promotionCode)) {
      cookie.remove(COOKIE_KEYS.promotionCode);
    }
  }, []);

  useEffect(() => {
    if (showModalOnLoad && !cookie.has(COOKIE_KEYS.viewedRetailOfferModal)) {
      setModalIsOpen(true);

      cookie.set(COOKIE_KEYS.viewedRetailOfferModal, 'true', {
        expires: 1,
      });
    }
  }, []);

  if (hideIfVanity && vanityBannerText) {
    return (
      <VanityBanner>
        <Banner text={vanityBannerText} />
      </VanityBanner>
    );
  }

  if (!text || hideBanner) {
    return null;
  }

  const isClickable = !!modalProps || !!text;
  const navigateToApp = () => navExternalWithQueryString(APP_URLS.welcome);
  const trackBannerClick = () => {
    trackEvent(EVENTS.siteBannerClicked, {
      path: location.pathname,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
    });
  };
  const openModal = () => setModalIsOpen(true);
  const navigateToDestination = () => {
    if (modalProps) {
      openModal();
      return;
    }

    navigateToApp();
  };
  const handleTrackedDestination = () => {
    trackBannerClick();
    navigateToDestination();
  };
  const handleBannerClick: React.MouseEventHandler<HTMLDivElement> = (
    event,
  ) => {
    if (!isClickable) {
      return;
    }

    const clickedInteractiveChild =
      event.target instanceof Element ? event.target.closest('a,button') : null;
    if (clickedInteractiveChild) {
      return;
    }

    handleTrackedDestination();
  };
  const handleModalClose = () => setModalIsOpen(false);
  const promoFlag = useFlag<PromoFlag | undefined>(
    'curology-gift-with-purchase',
  );

  const bannerText = getBannerText(promoFlag, text);
  const showGiftWithPurchaseBanner = hasActiveGiftWithPurchase(promoFlag);
  const bannerIcon = showGiftWithPurchaseBanner ? 'gift' : iconName;
  const bannerTextColor = showGiftWithPurchaseBanner
    ? LOCAL_COLORS.surfaceWhite
    : textColor;
  const bannerBackgroundColor = showGiftWithPurchaseBanner
    ? LOCAL_COLORS.surfaceBrandBold
    : backgroundColor;

  if (!bannerText) {
    return null;
  }

  return (
    <React.Fragment>
      {modalProps &&
        modalIsOpen &&
        ('content' in modalProps ? (
          <CtaModal {...modalProps} onClose={handleModalClose} />
        ) : (
          <Modal {...(modalProps as ModalProps)} onClose={handleModalClose} />
        ))}

      {ldClientReady && (
        <NavBannerBox
          isClickable={isClickable}
          isFloating={!badge && !!floatingOnDesktop}
          hasBadge={!!badge}
          backgroundColor={bannerBackgroundColor}
          textColor={bannerTextColor}
          zIndex={props.aboveNavigation ? Z_SCALE.modal + 1 : 1}
          isClosable={isClosable}
          text={
            <React.Fragment>
              {bannerIcon && (
                <DynamicIcon size="sm" name={bannerIcon as IconName} />
              )}
              <span>{bannerText}</span>
            </React.Fragment>
          }
          ctaText={ctaText}
          onClick={isClickable ? handleBannerClick : undefined}
          onCtaClick={handleTrackedDestination}
          cookieKey={cookieKey}
        />
      )}

      {badge && (
        <Badge
          isClickable={!!modalProps}
          onClick={modalProps ? handleBannerClick : navigateToApp}
        >
          {badge}
        </Badge>
      )}
    </React.Fragment>
  );
};
